import { z } from 'zod';

// La memoire du foyer, etape B1 (2026-09-09).
//
// Un FAIT est une ligne que Colette retient : « notre gare est Gare du Nord »,
// « Lila sort de l'ecole a 16h30 », « allergique aux arachides ». Il se rattache a une
// personne du foyer (membre ou personne sans compte) ou au foyer entier.
//
// Ne PAS en faire un formulaire a champs (adresse, ecole, allergies…) : le premier
// foyer est a Dakar, le suivant a Abidjan — les cases qu'on imaginerait seraient
// francaises, donc fausses. Une ligne libre porte tout, et le cerveau la lit telle
// quelle.

/** Un fait tient en une ligne : au-dela, c'est une note, pas un fait. */
export const LONGUEUR_MAX_FAIT = 160;

/**
 * Le texte d'un fait : rabattu sur une ligne (retours et espaces multiples), borne.
 * L'ordre compte : on nettoie AVANT de juger, sinon « \n\n » passerait pour du contenu.
 */
export const texteDeFaitSchema = z
  .string()
  .transform((valeur) => valeur.replace(/\s+/g, ' ').trim())
  .pipe(
    z
      .string()
      .min(1, 'Écris ce que Colette doit retenir')
      .max(LONGUEUR_MAX_FAIT, `Un fait tient en une ligne : ${LONGUEUR_MAX_FAIT} caractères au plus`),
  );

/** A qui se rattache un fait. Le foyer n'a pas d'identifiant : il n'y en a qu'un. */
export const sujetDuFaitSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('foyer') }),
  z.object({ type: z.literal('membre'), id: z.string().min(1) }),
  z.object({ type: z.literal('personne'), id: z.string().min(1) }),
]);

/**
 * Le cercle d'un fait. Il suit la meme regle que tout le reste du foyer, avec une seule
 * simplification assumee : pas de « choisi » ici. Un fait choisi supposerait un
 * selecteur de personnes a l'ecran ; tant qu'il n'existe pas, l'API n'offre pas une
 * option que rien ne peut poser.
 */
export const cercleDuFaitSchema = z.enum(['HOUSEHOLD', 'PRIVATE'], {
  message: 'Cercle invalide pour un fait',
});

export const ajouterFaitSchema = z.object({
  sujet: sujetDuFaitSchema,
  texte: texteDeFaitSchema,
  /** Absent : le fait est au foyer. C'est le defaut, et c'est le cas courant. */
  visibility: cercleDuFaitSchema.optional(),
});

export const modifierFaitSchema = z.object({
  texte: texteDeFaitSchema,
});

export type CercleDuFait = z.infer<typeof cercleDuFaitSchema>;
export type SujetDuFait = z.infer<typeof sujetDuFaitSchema>;
export type AjouterFaitInput = z.infer<typeof ajouterFaitSchema>;
export type ModifierFaitInput = z.infer<typeof modifierFaitSchema>;
